import React, { useEffect, useState } from "react";
import { Plus, Pencil, X, FileText, Download, Send } from "lucide-react";
import { toast }            from "../../../ui/Toast";
import { useConfirmDelete } from "../../../../hooks/useConfirmDelete";

const API     = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls= "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls= "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const LETTER_TYPES = [
  "Offer Letter","Appointment Letter","Increment Letter","Promotion Letter",
  "Warning Letter","Show Cause Notice","Termination Letter","Resignation Acceptance",
  "Relieving Letter","Experience Certificate","Salary Certificate","NOC Letter","Custom",
];

const VARS = [
  "{{employee_name}}","{{employee_id}}","{{designation}}","{{department}}",
  "{{date}}","{{joining_date}}","{{salary}}","{{increment_amount}}","{{increment_pct}}",
  "{{manager_name}}","{{company_name}}","{{company_address}}","{{notice_period}}",
  "{{last_working_date}}","{{hr_name}}","{{hr_signature}}",
];

export default function LettersTab() {
  const [templates,  setTemplates]  = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [editing,    setEditing]    = useState(null);
  const [showForm,   setShowForm]   = useState(false);
  const [generating, setGenerating] = useState(null); // template being generated
  const [genEmpId,   setGenEmpId]   = useState("");
  const [draft,      setDraft]      = useState({ name: "", type: "Offer Letter", body: "" });
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState(null);
  const { confirmDelete, ConfirmDialog } = useConfirmDelete();
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const [tr, em] = await Promise.all([
        fetch(`${API}/api/admin/letter-templates`,                { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/admin/users?limit=200`,                 { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setTemplates(Array.isArray(tr) ? tr : []);
      setEmployees(Array.isArray(em) ? em : em?.users || []);
    } catch { setTemplates([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft.name || !draft.body) return;
    setSaving(true); setMsg(null);
    try {
      const isNew = !editing;
      const res = await fetch(
        isNew ? `${API}/api/admin/letter-templates` : `${API}/api/admin/letter-templates/${editing}`,
        { method: isNew ? "POST" : "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(draft) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "✓ Template saved." });
      setShowForm(false); setDraft({ name: "", type: "Offer Letter", body: "" }); setEditing(null);
      await load();
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  const del = (id) => confirmDelete("this template", async () => {
    try {
      const res = await fetch(`${API}/api/admin/letter-templates/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTemplates(t => t.filter(x => x.id !== id));
      toast.success("Template deleted");
    } catch {
      toast.error("Failed to delete template");
    }
  });

  const generate = async (template) => {
    if (!genEmpId) return;
    try {
      const res = await fetch(`${API}/api/admin/letter-templates/${template.id}/generate?employee_id=${genEmpId}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `${template.name.replace(/\s+/g,"_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      setGenerating(null); setGenEmpId("");
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
  };

  const insertVar = (v) => setDraft(d => ({ ...d, body: (d.body || "") + v }));
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const startEdit = (t) => { setDraft({ ...t }); setEditing(t.id); setShowForm(true); };

  return (
    <div className="space-y-5">

      {/* Template editor */}
      {showForm && (
        <div className={`${card} p-5 space-y-4`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {editing ? "Edit Template" : "New Letter Template"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Template Name</label>
              <input className={inputCls} value={draft.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Standard Offer Letter" />
            </div>
            <div>
              <label className={labelCls}>Letter Type</label>
              <select className={inputCls} value={draft.type} onChange={e => set("type", e.target.value)}>
                {LETTER_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Variable palette */}
          <div>
            <label className={labelCls}>Insert Variable</label>
            <div className="flex flex-wrap gap-1.5">
              {VARS.map(v => (
                <button key={v} onClick={() => insertVar(v)}
                  className="text-xs px-2 py-0.5 rounded-full border border-[#00ff90]/15 bg-[#00ff90]/5 text-[#00ff90]/50 hover:bg-[#00ff90]/10 hover:text-[#00ff90]/70 transition-colors">
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Letter Body</label>
            <textarea rows={14} className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none font-mono"
              value={draft.body || ""} onChange={e => set("body", e.target.value)}
              placeholder="Dear {{employee_name}},&#10;&#10;We are pleased to offer you the position of {{designation}}…" />
          </div>

          {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => { setShowForm(false); setDraft({ name: "", type: "Offer Letter", body: "" }); setEditing(null); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving || !draft.name || !draft.body}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : "Save Template"}
            </button>
          </div>
        </div>
      )}

      {/* Template list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Letter Templates</span>
          <button onClick={() => { setDraft({ name: "", type: "Offer Letter", body: "" }); setEditing(null); setShowForm(v => !v); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> New Template
          </button>
        </div>

        <div className="divide-y divide-white/5">
          {loading ? Array.from({length:3}).map((_,i) => (
            <div key={i} className="px-5 py-4"><div className="h-10 rounded-lg bg-white/5 animate-pulse" /></div>
          )) : templates.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No templates yet.</p>
          ) : templates.map(t => (
            <div key={t.id}>
              <div className="flex items-center justify-between gap-3 px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={15} className="text-[#00ff90]/50 shrink-0" />
                  <div>
                    <p className="text-sm text-white/75 font-medium">{t.name}</p>
                    <p className="text-xs text-white/30">{t.type}</p>
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => setGenerating(generating === t.id ? null : t.id)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                    <Download size={11} /> Generate
                  </button>
                  <button onClick={() => startEdit(t)}
                    className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => del(t.id)}
                    className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                    <X size={12} />
                  </button>
                </div>
              </div>

              {/* Generate panel */}
              {generating === t.id && (
                <div className="px-5 pb-4 flex items-center gap-3 bg-[#00ff90]/3 border-t border-[#00ff90]/10">
                  <div className="flex-1">
                    <label className={labelCls}>Select Employee</label>
                    <select className={inputCls} value={genEmpId} onChange={e => setGenEmpId(e.target.value)}>
                      <option value="">Choose employee…</option>
                      {employees.map(e => <option key={e.id} value={e.id}>{e.full_name || e.name}</option>)}
                    </select>
                  </div>
                  <button onClick={() => generate(t)} disabled={!genEmpId}
                    className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors self-end">
                    <Download size={12} /> Download PDF
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {msg && !showForm && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"}`}>
          {msg.text}
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
}